import { useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { PACK_LIBELLES, estPack } from '../../../shared/business-logic/abonnement.ts';

interface Paiement {
  id: string;
  csb_id: string;
  csb_nom: string;
  reference_transaction: string;
  montant: string;
  devise: string;
  statut: 'EN_ATTENTE' | 'CONFIRME' | 'REJETE';
  date_transaction: string;
  confirme_le: string | null;
  motif_rejet: string | null;
  pack: string | null;
  duree_mois: number | null;
}

const FILTRES = [
  { valeur: 'EN_ATTENTE', libelle: 'À traiter' },
  { valeur: 'CONFIRME', libelle: 'Confirmés' },
  { valeur: 'REJETE', libelle: 'Rejetés' },
  { valeur: 'TOUS', libelle: 'Tous' },
];

export default function PaiementsAdmin() {
  const [filtre, setFiltre] = useState('EN_ATTENTE');
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [idEnCours, setIdEnCours] = useState<string | null>(null);
  const [idRejetOuvert, setIdRejetOuvert] = useState<string | null>(null);
  const [motif, setMotif] = useState('');

  useEffect(() => {
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtre]);

  async function charger() {
    setChargement(true);
    setErreur(null);
    try {
      const data = await api.get<Paiement[]>(`/admin/paiements?statut=${filtre}`);
      setPaiements(data);
    } catch (err: any) {
      setErreur(err.message ?? 'Impossible de charger les paiements');
    } finally {
      setChargement(false);
    }
  }

  async function confirmer(id: string) {
    setIdEnCours(id);
    try {
      await api.post(`/admin/paiements/${id}/confirmer`);
      await charger();
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur lors de la confirmation');
    } finally {
      setIdEnCours(null);
    }
  }

  async function rejeter(id: string) {
    if (motif.trim().length < 3) return;
    setIdEnCours(id);
    try {
      await api.post(`/admin/paiements/${id}/rejeter`, { motif: motif.trim() });
      setIdRejetOuvert(null);
      setMotif('');
      await charger();
    } catch (err: any) {
      setErreur(err.message ?? 'Erreur lors du rejet');
    } finally {
      setIdEnCours(null);
    }
  }

  return (
    <div>
      <h1>Paiements à valider</h1>

      <div className="chip-grid" style={{ marginBottom: 16 }}>
        {FILTRES.map((f) => (
          <div key={f.valeur} className={'chip' + (filtre === f.valeur ? ' selected' : '')} onClick={() => setFiltre(f.valeur)}>
            {f.libelle}
          </div>
        ))}
      </div>

      {erreur && <p className="messages-erreur">{erreur}</p>}
      {chargement && <p>Chargement…</p>}
      {!chargement && paiements.length === 0 && <p>Aucun paiement dans cette catégorie.</p>}

      {paiements.map((p) => (
        <div key={p.id} style={{ background: 'white', border: '1px solid #dcdcdc', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <p style={{ margin: 0 }}><strong>{p.csb_nom}</strong> — {p.montant} {p.devise}</p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem' }}>{estPack(p.pack) ? `${PACK_LIBELLES[p.pack]} · ${p.duree_mois ?? 1} mois` : 'Ancien paiement (Tout Activer · 1 mois)'}</p>
          <p style={{ margin: '4px 0', fontSize: '0.9rem', color: '#555' }}>
            Référence : {p.reference_transaction} · {new Date(p.date_transaction).toLocaleString('fr-FR')}
          </p>
          {p.statut === 'REJETE' && p.motif_rejet && (
            <p className="messages-erreur" style={{ fontSize: '0.9rem' }}>Motif du rejet : {p.motif_rejet}</p>
          )}

          {p.statut === 'EN_ATTENTE' && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => confirmer(p.id)} disabled={idEnCours === p.id}>
                {idEnCours === p.id ? '…' : '✅ Confirmer'}
              </button>
              <button className="danger" onClick={() => setIdRejetOuvert(p.id)} disabled={idEnCours === p.id}>
                ✕ Rejeter
              </button>

              {idRejetOuvert === p.id && (
                <div style={{ marginTop: 10 }}>
                  <label htmlFor={`motif-${p.id}`}>Motif du rejet</label>
                  <input
                    id={`motif-${p.id}`}
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    placeholder="ex: référence introuvable sur le relevé MVola"
                  />
                  <div style={{ marginTop: 8 }}>
                    <button onClick={() => rejeter(p.id)} disabled={motif.trim().length < 3 || idEnCours === p.id}>
                      Confirmer le rejet
                    </button>
                    <button className="secondaire" onClick={() => { setIdRejetOuvert(null); setMotif(''); }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
